import React, { useEffect, useState, useContext } from "react";
import "./PendingAmendmentFormLog.css";

import { useMsal } from "@azure/msal-react";
import { getAPICall, postAPICall } from "../../../config/apiCalls";
import { UserContext } from "../../../context/UserContext";

import { CircularProgress, IconButton } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";
import PendingAmendmentFormLogDetails from "./PendingAmendmentFormLogDetails/PendingAmendmentFormLogDetails";

import {
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";
import { convertToPST } from "../../../constants/dateTime";

const PendingAmendmentFormLog = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	const [logs, setLogs] = useState([]);
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	const [detailsOpen, setDetailsOpen] = useState(false);
	const [recordToView, setRecordToView] = useState(null);

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Pending Amendment Form Log",
			employeeID: employeeID,
			notes: "Accessed the Pending Amendment Form Log page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchLogs();
	}, []);

	useEffect(() => {
		createColumns();
		createRows();
	}, [logs]);

	const fetchLogs = async () => {
		setIsLoading(true);
		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/pendingAmendments"
			);
			setLogs(response.data || []);
		} catch (error) {
			console.error(error);
		}
		setIsLoading(false);
	};

	const createColumns = () => {
		setColumns([
			{
				accessorKey: "action_time",
				header: () => <span>Action Time</span>,
				label: "Action Time",
				cell: (props) => <span>{props.getValue() || notApplicable}</span>,
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "submitted_by",
				header: () => <span>Submitted By</span>,
				label: "Submitted By",
				cell: (props) => <span>{props.getValue() || notApplicable}</span>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "action_type",
				header: () => <span>Action</span>,
				label: "Action",
				cell: (props) => <span>{props.getValue() || notApplicable}</span>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "action_status",
				header: () => <span>Status</span>,
				label: "Status",
				cell: (props) => {
					const value = props.getValue();
					return (
						<span style={{ color: value === "failure" ? "red" : "black" }}>
							{value || notApplicable}
						</span>
					);
				},
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "deal_name",
				header: () => <span>Deal Name</span>,
				label: "Deal Name",
				cell: (props) => <span>{props.getValue() || notApplicable}</span>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "error_message",
				header: () => <span>Error Message</span>,
				label: "Error Message",
				cell: (props) => (
					<span style={{ color: props.getValue() ? "red" : "black" }}>
						{props.getValue() || notApplicable}
					</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "moreDetails",
				header: "",
				label: "More Details",
				cell: ({ row }) => (
					<IconButton
						onClick={() => detailsClicked(row.original.record)}
						disableRipple={true}
						sx={{ justifyContent: "flex-end" }}
					>
						<MoreHorizIcon fontSize="small" />
					</IconButton>
				),
				enableColumnFilter: false,
				enableSorting: false,
			},
		]);
	};

	const createRows = () => {
		setRows(
			logs.map((log) => ({
				action_time: convertToPST(log.action_completed_time || log.created),
				submitted_by:
					log.submitted_by_employee_name ||
					log.submitted_by_employee_name_lookup ||
					notApplicable,
				action_type: log.action_type,
				action_status: log.action_status,
				deal_name: log.hubspot_deal_name || notApplicable,
				error_message: log.error_message,
				record: log,
			}))
		);
	};

	const detailsClicked = (record) => {
		setRecordToView(record);
		setDetailsOpen(true);
	};

	return (
		<div className="PendingAmendmentFormLog">
			<div className="PendingAmendmentFormLogHeader">
				<div className="PendingAmendmentFormLogHeaderTitle">
					Pending Amendment Form Logs
				</div>
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{logs.length === 0 && <J2NoResultsFound />}
					{logs.length > 0 && columns.length > 0 && rows.length > 0 && (
						<TanStackTable columnData={columns} rowData={rows} />
					)}
				</div>
			)}
			{detailsOpen && recordToView && (
				<PendingAmendmentFormLogDetails
					open={detailsOpen}
					recordToView={recordToView}
					setOpen={setDetailsOpen}
				/>
			)}
		</div>
	);
};

export default PendingAmendmentFormLog;
